import React from 'react'
import PropTypes from 'prop-types'
import { useParams, useHistory } from 'react-router-dom'
import { Trash2Icon, CalendarDays } from 'lucide-react'
import pluralize from 'pluralize'
import { Button } from '../../ui/button'
import Avatar from '../../leevo_ui/Avatar/Avatar'
import Badge from '../../leevo_ui/Badge/Badge'
import { levelArrayToString } from '../../../utilities/helpers'

function ClassesTable({ klasses, onEditClick, onDeleteClick }) {
  const { facility_id: facilityId } = useParams()
  const history = useHistory()

  function getEnrollmentBadge(studentsCount, maxStudents) {
    let spotsLeft
    if (maxStudents !== null && maxStudents !== undefined) {
      const difference = maxStudents - studentsCount
      spotsLeft = difference > 0 ? difference : 0
    }

    let badgeColor
    let badgeText

    if (maxStudents === null || maxStudents === undefined) {
      badgeColor = 'green'
      badgeText = 'Available'
    } else if (spotsLeft >= 4) {
      badgeColor = 'green'
      badgeText = `${spotsLeft} ${pluralize('spot', spotsLeft)} left`
    } else if (spotsLeft > 0) {
      badgeColor = 'orange'
      badgeText = `${spotsLeft} ${pluralize('spot', spotsLeft)} left`
    } else {
      badgeColor = 'red'
      badgeText = 'Full'
    }

    return { color: badgeColor, text: badgeText }
  }

  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Schedule
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Date Range
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Coaches
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Skill Levels
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Enrollment
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Packages
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-right text-gray-500 uppercase"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {klasses.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                  No classes found
                </td>
              </tr>
            ) : (
              klasses.map((klass) => {
                const badge = getEnrollmentBadge(klass.studentsCount, klass.maxStudents)

                return (
                  <tr key={klass.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900">{klass.dayOfWeek}</span>
                        {klass.multiDay && (
                          <CalendarDays className="h-3.5 w-3.5 text-purple-500" />
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{klass.timeOfDay}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{klass.startDate}</div>
                      <div className="text-sm text-gray-500">to {klass.endDate}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {klass.coaches.map((coach) => (
                          <div key={coach.id} className="flex items-center gap-1.5">
                            <Avatar
                              fullName={coach.fullName}
                              cloudinaryPhotoPublicId={coach.cloudinaryPhotoPublicId}
                              alt={`Photo of ${coach.fullName}`}
                              userId={coach.id}
                              size={5}
                              className="inline-block rounded-full shrink-0"
                            />
                            <span className="text-sm text-gray-700 truncate max-w-[80px]">
                              {coach.preferredName || coach.fullName?.split(' ')[0]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-500">
                        {klass.supportedSkillLevels.length === 0
                          ? 'Any'
                          : levelArrayToString(
                              klass.supportedSkillLevels.map((s) => s.name),
                              { maxLength: 20 }
                            )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <div className="text-sm text-gray-900 mb-1">
                        <span className="font-semibold">{klass.studentsCount}</span>{' '}
                        {pluralize('student', klass.studentsCount)}
                      </div>
                      <Badge text={badge.text} color={badge.color} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-500">
                        {klass.klassPackages.length === 0
                          ? 'None'
                          : klass.klassPackages.map((pkg) => pkg.title).join(', ')}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEditClick(klass.id)}
                          className="h-8"
                          data-cy="edit"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => history.push(`/facilities/${facilityId}/classes/new?copy_id=${klass.id}`)}
                          className="h-8"
                          data-cy="copy"
                        >
                          Copy
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => onDeleteClick(klass)}
                          className="h-8"
                          data-cy="delete"
                        >
                          <Trash2Icon className="w-3.5 h-3.5 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="p-3 space-y-3 lg:hidden">
        {klasses.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">No classes found</div>
        ) : (
          klasses.map((klass) => {
            const badge = getEnrollmentBadge(klass.studentsCount, klass.maxStudents)

            return (
              <div key={klass.id} className="p-3 bg-white border border-gray-200 rounded-lg" data-cy="klass-item">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
                      {klass.dayOfWeek} - {klass.timeOfDay}
                      {klass.multiDay && (
                        <CalendarDays className="h-3.5 w-3.5 text-purple-500" />
                      )}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {klass.startDate} to {klass.endDate}
                    </p>
                  </div>
                  <Badge text={badge.text} color={badge.color} />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Enrollment</label>
                    <p className="text-sm text-gray-900">
                      <span className="font-semibold">{klass.studentsCount}</span>{' '}
                      {pluralize('student', klass.studentsCount)}
                    </p>
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Skill Levels</label>
                    <p className="text-sm text-gray-500">
                      {klass.supportedSkillLevels.length === 0
                        ? 'Any'
                        : levelArrayToString(
                            klass.supportedSkillLevels.map((s) => s.name),
                            { maxLength: 15 }
                          )}
                    </p>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Coaches</label>
                  <div className="flex flex-col gap-1">
                    {klass.coaches.map((coach) => (
                      <div key={coach.id} className="flex items-center gap-1.5">
                        <Avatar
                          fullName={coach.fullName}
                          cloudinaryPhotoPublicId={coach.cloudinaryPhotoPublicId}
                          alt={`Photo of ${coach.fullName}`}
                          userId={coach.id}
                          size={5}
                          className="inline-block rounded-full shrink-0"
                        />
                        <span className="text-sm text-gray-700 truncate max-w-[120px]">
                          {coach.preferredName || coach.fullName?.split(' ')[0]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {klass.klassPackages.length > 0 && (
                  <div className="mb-3">
                    <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Packages</label>
                    <p className="text-sm text-gray-500">{klass.klassPackages.map((pkg) => pkg.title).join(', ')}</p>
                  </div>
                )}

                <div className="flex flex-col gap-2 pt-3 mt-3 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEditClick(klass.id)}
                    className="w-full h-9"
                    data-cy="edit"
                  >
                    Edit
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => history.push(`/facilities/${facilityId}/classes/new?copy_id=${klass.id}`)}
                      className="flex-1 h-9"
                      data-cy="copy"
                    >
                      Copy
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDeleteClick(klass)}
                      className="flex-1 h-9"
                      data-cy="delete"
                    >
                      <Trash2Icon className="w-3.5 h-3.5 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </>
  )
}

ClassesTable.propTypes = {
  klasses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      dayOfWeek: PropTypes.string,
      timeOfDay: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      coaches: PropTypes.array,
      supportedSkillLevels: PropTypes.array,
      students: PropTypes.array,
      maxStudents: PropTypes.number,
      klassPackages: PropTypes.array
    })
  ).isRequired,
  onEditClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired
}

export default ClassesTable
